import { Link } from "react-router-dom";

// Inline formatting: **bold** only (all our legal docs need).
const renderInline = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-white">{part}</strong> : part
  );

// Minimal Markdown renderer for our legal documents. Supports the exact subset
// these docs use: # / ## headings, paragraphs, "- " lists (with wrapped
// continuation lines), **bold**, and a single pipe table.
const renderMarkdown = (md) => {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    if (line.startsWith("# ")) {
      out.push(<h1 key={key++} className="text-3xl font-bold text-white font-['Chivo'] mb-2">{renderInline(line.slice(2))}</h1>);
      i++; continue;
    }
    if (line.startsWith("## ")) {
      out.push(<h2 key={key++} className="text-lg font-semibold text-[#f4cc1e] mt-8 mb-2">{renderInline(line.slice(3))}</h2>);
      i++; continue;
    }

    // Table: header row followed by a |---| separator
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?\s*-{2,}/.test(lines[i + 1])) {
      const cells = (row) => row.split("|").map((c) => c.trim()).filter((c) => c.length);
      const header = cells(line);
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && lines[i].includes("|")) { rows.push(cells(lines[i])); i++; }
      out.push(
        <div key={key++} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>{header.map((h, j) => (
                <th key={j} className="text-left text-[#f4cc1e] font-semibold border-b border-[#1a3a6a] py-2 pr-4">{renderInline(h)}</th>
              ))}</tr>
            </thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => (
                <td key={ci} className="border-b border-[#1a3a6a]/60 py-2 pr-4 text-white/80 align-top">{renderInline(c)}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bulleted list (fold wrapped continuation lines into the current item)
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith("- ") || (/^\s+\S/.test(lines[i]) && items.length))) {
        if (lines[i].startsWith("- ")) items.push(lines[i].slice(2));
        else items[items.length - 1] += " " + lines[i].trim();
        i++;
      }
      out.push(
        <ul key={key++} className="list-disc pl-5 space-y-1.5 my-3 text-white/80">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }

    // Paragraph (accumulate consecutive plain lines)
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith("#") && !lines[i].startsWith("- ") && !lines[i].includes("|")) {
      para.push(lines[i]); i++;
    }
    out.push(<p key={key++} className="text-white/80 leading-relaxed my-3">{renderInline(para.join(" "))}</p>);
  }
  return out;
};

const LegalDoc = ({ content }) => (
  <div className="min-h-screen bg-[#091c3c] text-white">
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <img src="/roofnuts-logo.png" alt="RoofNuts" className="h-10 w-auto" />
        <Link to="/login" className="text-sm text-[#f4cc1e] hover:underline">← Back to Portal</Link>
      </div>
      <article className="text-[15px]">{renderMarkdown(content)}</article>
      <div className="mt-12 pt-6 border-t border-[#1a3a6a] flex flex-wrap gap-4 text-sm text-white/50">
        <Link to="/eula" className="hover:text-[#f4cc1e]">End User License Agreement</Link>
        <Link to="/privacy" className="hover:text-[#f4cc1e]">Privacy Policy</Link>
        <a href="https://roofnutscalling.com" className="hover:text-[#f4cc1e]">roofnutscalling.com</a>
      </div>
      <p className="mt-3 text-xs text-white/40">
        Website by{" "}
        <a
          href="https://parker-local.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[#f4cc1e]"
        >
          Parker Local Web Design
        </a>
      </p>
    </div>
  </div>
);

export default LegalDoc;
